import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle, KeyRound } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function RestablecerContrasena() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [listo, setListo] = useState(false);
    // El backend indica a qué login corresponde según el tipo de cuenta
    const [destino, setDestino] = useState('/login');
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
        if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
        setLoading(true);
        try {
            const res = await axios.post(`${BASE}/api/v1/auth/password-reset/confirm/`, {
                token,
                new_password: password,
            });
            if (res.data?.destino) setDestino(res.data.destino);
            setListo(true);
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo restablecer la contraseña.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pantalla-centrada">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="bg-white rounded-3xl shadow-xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="bg-brand-100 p-3 rounded-2xl">
                            <KeyRound size={24} className="text-brand-800" />
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-slate-900 tracking-tight">Nueva contraseña</h1>
                            <p className="text-xs text-slate-500 font-medium">Definí tu clave de acceso</p>
                        </div>
                    </div>

                    {!token ? (
                        <div className="bg-error-50 border border-error-200 rounded-2xl p-6 text-center">
                            <AlertCircle size={32} className="text-error-500 mx-auto mb-3" />
                            <p className="font-black text-error-800">Link inválido</p>
                            <p className="text-sm text-error-700 mt-1">
                                Este link no tiene un token válido. Pedí uno nuevo desde la pantalla de recuperación.
                            </p>
                            <Link
                                to="/olvide-contrasena"
                                className="inline-block mt-4 text-sm font-semibold text-brand-800 hover:text-brand-800"
                            >
                                Pedir un link nuevo
                            </Link>
                        </div>
                    ) : listo ? (
                        <div className="text-center space-y-4">
                            <div className="bg-exito-50 border border-exito-200 rounded-2xl p-6">
                                <CheckCircle size={32} className="text-exito-500 mx-auto mb-3" />
                                <p className="font-black text-exito-800">¡Contraseña actualizada!</p>
                                <p className="text-sm text-exito-700 mt-1">Ya podés iniciar sesión con tu clave nueva.</p>
                            </div>
                            <button
                                onClick={() => navigate(destino)}
                                className="w-full py-3 px-4 rounded-xl font-semibold text-negro-950 bg-brand-600 hover:bg-brand-500 shadow-lg shadow-brand-500/30 transition-all"
                            >
                                Ir al login
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="bg-error-50 text-error-600 p-3 rounded-xl text-sm font-medium border border-error-100 flex items-center gap-2">
                                    <AlertCircle size={16} />
                                    {error}
                                </div>
                            )}

                            <div>
                                <label htmlFor="clave-nueva" className="etiqueta-simple">Contraseña nueva</label>
                                <div className="relative">
                                    <div className="icono-campo-interno">
                                        <Lock size={18} />
                                    </div>
                                    <input id="clave-nueva"
                                        required
                                        type="password"
                                        autoComplete="new-password"
                                        className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-slate-50 font-medium transition-colors"
                                        placeholder="Mínimo 8 caracteres"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="clave-repetida" className="etiqueta-simple">Repetir contraseña</label>
                                <div className="relative">
                                    <div className="icono-campo-interno">
                                        <Lock size={18} />
                                    </div>
                                    <input id="clave-repetida"
                                        required
                                        type="password"
                                        autoComplete="new-password"
                                        className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-slate-50 font-medium transition-colors"
                                        placeholder="Repetí la contraseña"
                                        value={confirm}
                                        onChange={e => setConfirm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-3 px-4 rounded-xl shadow-lg shadow-brand-500/30 font-semibold text-negro-950 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 transition-all"
                            >
                                {loading ? 'Guardando...' : 'Guardar contraseña'}
                            </button>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
